import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { getLiveInterviewSessions } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const MyReportsPage = () => {
  const navigate = useNavigate();
  const { currentSession, isLoading: authLoading } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading before checking session
    if (authLoading) {
      return;
    }
    
    if (!currentSession || currentSession.role !== 'candidate') {
      console.log('❌ MyReportsPage: No valid session, redirecting to login', { currentSession });
      navigate('/login');
      return;
    }
    
    console.log('✅ MyReportsPage: Valid session found', { currentSession });
    loadTrainingSessions();
  }, [currentSession, authLoading, navigate]);

  const loadTrainingSessions = async () => {
    try {
      setLoading(true);
      
      // Get multiple possible identifiers from current session
      const candidateId = currentSession?.user_id || currentSession?.candidate_id;
      const candidateEmail = currentSession?.email;
      const candidateName = currentSession?.profile?.full_name || currentSession?.full_name || 
                           `${currentSession?.profile?.first_name || ''} ${currentSession?.profile?.last_name || ''}`.trim();
      
      console.log('🔍 Loading sessions for candidate:', { 
        candidateId, 
        candidateEmail, 
        candidateName,
        fullSession: currentSession 
      });
      
      const baseUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

      const params = new URLSearchParams();
      if (candidateId) params.set('candidate_id', candidateId);
      if (candidateEmail) params.set('candidate_email', candidateEmail);
      const queryString = params.toString();

      // Fetch from both AI trainer (text-based) and live interview (voice) sessions
      const [aiTrainerResponse, liveInterviewResponse] = await Promise.all([
        // Old text-based trainer sessions
        candidateId
          ? fetch(`${baseUrl}/ai-trainer/candidate-sessions/${candidateId}`).then(r => r.json()).catch(() => ({ sessions: [] }))
          : Promise.resolve({ sessions: [] }),
        // Live voice trainer sessions (stored in live_interview_sessions table)
        fetch(`${baseUrl}/live-interview/candidate-sessions${queryString ? `?${queryString}` : ''}`).then(r => r.json()).catch(() => ({ sessions: [] }))
      ]);

      console.log('🔍 AI Trainer API response:', aiTrainerResponse);
      console.log('🔍 Live Interview API response:', liveInterviewResponse);

      // Filter live sessions to only show trainer type (not real job interviews)
      const liveTrainerSessions = (liveInterviewResponse.sessions || [])
        .filter(s => s.interview_type === 'trainer')
        .map(s => ({
          ...s,
          source: 'live_interview',  // Mark source for report viewing
          session_type: 'live_voice'
        }));

      // Mark old trainer sessions with source
      const oldTrainerSessions = (aiTrainerResponse.sessions || []).map(s => ({
        ...s,
        source: 'ai_trainer'
      }));

      // Merge and sort by date (newest first)
      const allSessions = [...oldTrainerSessions, ...liveTrainerSessions].sort((a, b) => {
        const dateA = new Date(a.completed_at || a.created_at || 0);
        const dateB = new Date(b.completed_at || b.created_at || 0);
        return dateB - dateA;
      });

      setSessions(allSessions);
      console.log('✅ Total training sessions loaded:', allSessions.length, `(${oldTrainerSessions.length} text + ${liveTrainerSessions.length} live)`);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (sessionId, source) => {
    try {
      setLoadingDetails(true);
      setSelectedSession(sessionId);
      const baseUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

      // Use correct endpoint based on session source
      const reportUrl = source === 'live_interview'
        ? `${baseUrl}/live-interview/session/${sessionId}/report`
        : `${baseUrl}/ai-trainer/session/${sessionId}/report`;

      const response = await fetch(reportUrl);

      if (!response.ok) {
        throw new Error('Failed to load session report');
      }

      const reportData = await response.json();
      
      // Extract comprehensive evaluation data
      const evaluation = reportData.evaluation || {};
      const transcript = reportData.transcript || [];
      
      // Transform the data to include all evaluation details
      const transformedData = {
        // Basic session info
        session_id: reportData.session_id,
        candidate_name: reportData.candidate_name,
        candidate_email: reportData.candidate_email,
        job_title: reportData.job_title || 'Training Session',
        company: reportData.company || 'Training',
        interview_type: reportData.interview_type || 'trainer',
        duration_minutes: reportData.duration_minutes || 5,
        status: reportData.status,
        started_at: reportData.started_at,
        completed_at: reportData.completed_at,
        
        // Scoring data - Use overall_percentage if available, otherwise overall_score
        overall_score: evaluation.overall_percentage || reportData.overall_score || 0,
        overall_grade: reportData.overall_grade || 'F',
        
        // Conversation data
        transcript: transcript,
        transcript_length: reportData.transcript_length || 0,
        conversation_turns: Math.floor((reportData.transcript_length || 0) / 2),
        
        // Evaluation details
        evaluation: evaluation,
        comprehensive_feedback: evaluation.detailed_feedback || evaluation.comprehensive_feedback || '',
        strengths: evaluation.strengths || [],
        areas_for_improvement: evaluation.weaknesses || evaluation.areas_for_improvement || [],
        recommendations: evaluation.actionable_tips || evaluation.recommendations || evaluation.recommendation || '',
        technical_strengths: evaluation.technical_strengths || [],
        
        // Skills assessment (enhanced format)
        skills_assessment: evaluation.skills_assessment || [],
        
        // Individual questions (new feature)
        individual_questions: evaluation.individual_questions || [],
        
        // Question scores (from old evaluator - for backward compatibility)
        question_scores: evaluation.question_scores || [],
        
        // Enhanced metrics
        conversation_metrics: evaluation.conversation_metrics || {},
        interview_readiness: evaluation.interview_readiness || {},
        suitability_score: evaluation.suitability_score || 0,
        domain_scores: evaluation.domain_scores || {},
        key_observations: evaluation.key_observations || []
      };
      
      setSessionDetails(transformedData);
    } catch (error) {
      console.error('Error loading session details:', error);
      alert('Failed to load session details. Please try again.');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseReport = () => {
    setSelectedSession(null);
    setSessionDetails(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 90) return 'bg-green-100 dark:bg-green-900/20';
    if (score >= 80) return 'bg-blue-100 dark:bg-blue-900/20';
    if (score >= 70) return 'bg-yellow-100 dark:bg-yellow-900/20';
    if (score >= 60) return 'bg-orange-100 dark:bg-orange-900/20';
    return 'bg-red-100 dark:bg-red-900/20';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              {authLoading ? 'Checking authentication...' : 'Loading your training reports...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Session Details Modal
  if (selectedSession && sessionDetails) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Training Report</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Session completed on {formatDate(sessionDetails.completed_at)}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={handleCloseReport}
              className="flex items-center gap-2"
            >
              <Icon name="close" size={18} />
              Close Report
            </Button>
          </div>

          {/* Overall Score Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Score Display */}
              <div className="lg:col-span-1 text-center">
                <div className={`text-6xl font-bold mb-4 ${getScoreColor(sessionDetails.overall_score)}`}>
                  {Math.round(sessionDetails.overall_score)}%
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Grade: {sessionDetails.overall_grade}
                </div>
                <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getScoreBgColor(sessionDetails.overall_score)}`}>
                  {sessionDetails.overall_score >= 80 ? '🌟 Interview Ready' :
                   sessionDetails.overall_score >= 70 ? '👍 Good Progress' :
                   sessionDetails.overall_score >= 60 ? '💪 Keep Practicing' :
                   '📚 More Practice Needed'}
                </div>
              </div>
              
              {/* Session Info */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Position</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{sessionDetails.job_title}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Duration</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{sessionDetails.duration_minutes} minutes</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Conversation Turns</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{sessionDetails.conversation_turns} exchanges</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Interview Type</div>
                    <div className="font-semibold text-gray-900 dark:text-white capitalize">{sessionDetails.interview_type}</div>
                  </div>
                </div>
                
                {/* Suitability Score */}
                {sessionDetails.suitability_score > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">Role Suitability</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${sessionDetails.suitability_score}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {Math.round(sessionDetails.suitability_score)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Individual Questions Analysis */}
          {sessionDetails.individual_questions && sessionDetails.individual_questions.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="quiz" size={28} className="text-purple-600" />
                Individual Question Analysis
              </h2>
              <div className="space-y-6">
                {sessionDetails.individual_questions.map((question, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    {/* Question Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                          Question {question.question_number}: {question.question}
                        </h3>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(question.score)} ${getScoreColor(question.score)}`}>
                        {question.score.toFixed(1)}%
                      </div>
                    </div>
                    
                    {/* Answer */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Answer:</h4>
                      <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                          "{question.answer}"
                        </p>
                      </div>
                    </div>
                    
                    {/* Evaluation */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Evaluation:</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {question.explanation}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Better Approach:</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {question.better_approach}
                        </p>
                      </div>
                    </div>
                    
                    {/* Strengths and Missing Elements */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {question.strengths_shown && question.strengths_shown.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Strengths Shown:</h4>
                          <ul className="text-sm text-green-600 dark:text-green-400 space-y-1">
                            {question.strengths_shown.map((strength, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <Icon name="check_circle" size={14} />
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {question.key_missing_elements && question.key_missing_elements.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">Missing Elements:</h4>
                          <ul className="text-sm text-orange-600 dark:text-orange-400 space-y-1">
                            {question.key_missing_elements.map((element, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <Icon name="warning" size={14} />
                                {element}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced Conversation Metrics */}
          {sessionDetails.conversation_metrics && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="analytics" size={28} className="text-blue-600" />
                Conversation Metrics
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {sessionDetails.conversation_metrics.total_questions || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Questions Asked</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {sessionDetails.conversation_metrics.questions_answered || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Questions Answered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {sessionDetails.conversation_metrics.response_rate_percentage?.toFixed(1) || 0}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Response Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {sessionDetails.conversation_metrics.avg_response_length_words || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Avg Words/Response</div>
                </div>
              </div>
              
              {/* Additional Metrics */}
              {(sessionDetails.conversation_metrics.engagement_score || sessionDetails.conversation_metrics.clarity_score || sessionDetails.conversation_metrics.depth_score) && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {sessionDetails.conversation_metrics.engagement_score && (
                      <div className="text-center">
                        <div className={`text-xl font-bold ${getScoreColor(sessionDetails.conversation_metrics.engagement_score)}`}>
                          {sessionDetails.conversation_metrics.engagement_score.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Engagement</div>
                      </div>
                    )}
                    {sessionDetails.conversation_metrics.clarity_score && (
                      <div className="text-center">
                        <div className={`text-xl font-bold ${getScoreColor(sessionDetails.conversation_metrics.clarity_score)}`}>
                          {sessionDetails.conversation_metrics.clarity_score.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Clarity</div>
                      </div>
                    )}
                    {sessionDetails.conversation_metrics.depth_score && (
                      <div className="text-center">
                        <div className={`text-xl font-bold ${getScoreColor(sessionDetails.conversation_metrics.depth_score)}`}>
                          {sessionDetails.conversation_metrics.depth_score.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Response Depth</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Skills Assessment */}
          {sessionDetails.skills_assessment && sessionDetails.skills_assessment.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="assessment" size={28} className="text-blue-600" />
                Skills Assessment
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {sessionDetails.skills_assessment.map((skill, index) => (
                  <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-900 dark:text-white">{skill.skill}</span>
                      <span className={`font-bold ${getScoreColor(skill.score)}`}>
                        {typeof skill.score === 'number' ? skill.score.toFixed(1) : skill.score}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          skill.score >= 80 ? 'bg-green-500' :
                          skill.score >= 70 ? 'bg-blue-500' :
                          skill.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${skill.score}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {skill.feedback}
                    </div>
                    {skill.improvement_tips && skill.improvement_tips.length > 0 && (
                      <div className="mt-3">
                        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Improvement Tips:</h4>
                        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          {skill.improvement_tips.map((tip, tipIndex) => (
                            <li key={tipIndex} className="flex items-start gap-1">
                              <Icon name="lightbulb" size={12} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Question Scores (for old evaluator data) */}
          {sessionDetails.question_scores && sessionDetails.question_scores.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="quiz" size={28} className="text-purple-600" />
                Question Performance
              </h2>
              <div className="space-y-4">
                {sessionDetails.question_scores.map((q, index) => (
                  <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white mb-1">
                          Question {index + 1}: {q.category || 'General'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {q.question || 'Interview question'}
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(q.score * 100)} ${getScoreColor(q.score * 100)}`}>
                        {Math.round(q.score * 100)}%
                      </div>
                    </div>
                    {q.feedback && (
                      <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                        {q.feedback}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comprehensive Feedback */}
          {sessionDetails.comprehensive_feedback && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="feedback" size={28} className="text-green-600" />
                AI Feedback
              </h2>
              <div className="prose prose-gray dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {sessionDetails.comprehensive_feedback}
                </p>
              </div>
            </div>
          )}

          {/* Actionable Tips & Recommendations */}
          {sessionDetails.recommendations && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="lightbulb" size={28} className="text-yellow-600" />
                Actionable Tips & Recommendations
              </h2>
              <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {Array.isArray(sessionDetails.recommendations) ? (
                  <ul className="space-y-3">
                    {sessionDetails.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-6 h-6 bg-purple-600 text-white rounded-full text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{sessionDetails.recommendations}</p>
                )}
              </div>
            </div>
          )}

          {/* Strengths and Weaknesses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Strengths */}
            {sessionDetails.strengths && sessionDetails.strengths.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                  <Icon name="star" size={28} className="text-green-600" />
                  Your Strengths
                </h2>
                <ul className="space-y-4">
                  {sessionDetails.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon name="check_circle" size={20} className="text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Areas for Improvement */}
            {sessionDetails.areas_for_improvement && sessionDetails.areas_for_improvement.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                  <Icon name="trending_up" size={28} className="text-blue-600" />
                  Areas to Improve
                </h2>
                <ul className="space-y-4">
                  {sessionDetails.areas_for_improvement.map((area, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon name="arrow_forward" size={20} className="text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Technical Strengths */}
          {sessionDetails.technical_strengths && sessionDetails.technical_strengths.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="code" size={28} className="text-purple-600" />
                Technical Strengths
              </h2>
              <ul className="space-y-3">
                {sessionDetails.technical_strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Icon name="check" size={16} className="text-purple-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Conversation Transcript */}
          {sessionDetails.transcript && sessionDetails.transcript.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Icon name="chat" size={28} className="text-indigo-600" />
                Conversation Transcript
              </h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {sessionDetails.transcript.map((turn, index) => (
                  <div key={index} className={`p-4 rounded-lg ${
                    turn.speaker === 'interviewer' || turn.speaker === 'AI' 
                      ? 'bg-blue-50 dark:bg-blue-900/20 ml-8' 
                      : 'bg-gray-50 dark:bg-gray-900/50 mr-8'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon 
                        name={turn.speaker === 'interviewer' || turn.speaker === 'AI' ? 'smart_toy' : 'person'} 
                        size={16} 
                        className={turn.speaker === 'interviewer' || turn.speaker === 'AI' ? 'text-blue-600' : 'text-gray-600'} 
                      />
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {turn.speaker === 'interviewer' || turn.speaker === 'AI' ? 'AI Trainer' : 'You'}
                      </span>
                      {turn.timestamp && (
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {new Date(turn.timestamp).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{turn.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <Button
              onClick={() => navigate('/ai-trainer')}
              className="flex items-center gap-2"
            >
              <Icon name="refresh" size={18} />
              Take Another Session
            </Button>
            <Button
              variant="secondary"
              onClick={handleCloseReport}
              className="flex items-center gap-2"
            >
              <Icon name="arrow_back" size={18} />
              Back to Reports
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main Reports List
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Training Reports</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              View your AI training session reports and track your progress
            </p>
          </div>
          <Button
            onClick={() => navigate('/ai-trainer')}
            className="flex items-center gap-2"
          >
            <Icon name="add" size={18} />
            New Training Session
          </Button>
        </div>

        {sessions.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full mb-6">
              <Icon name="assessment" size={40} className="text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              No Training Reports Yet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              You haven't completed any AI training sessions yet. Start your first session to see detailed reports here.
            </p>
            <Button
              onClick={() => navigate('/ai-trainer')}
              size="lg"
              className="flex items-center gap-2"
            >
              <Icon name="psychology" size={20} />
              Start Your First Training Session
            </Button>
          </div>
        ) : (
          /* Sessions List */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <div key={session.session_id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                {/* Session Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    session.status === 'completed' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'
                  }`}>
                    {session.status === 'completed' ? 'Completed' : 'In Progress'}
                  </div>
                  {session.overall_score && (
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(session.overall_score)} ${getScoreColor(session.overall_score)}`}>
                      {Math.round(session.overall_score)}%
                    </div>
                  )}
                </div>

                {/* Session Info */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {session.job_title}
                  </h3>
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <Icon name="category" size={16} />
                      <span className="capitalize">{session.source === 'live_interview' ? 'Live Voice' : (session.session_type || 'Training')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="quiz" size={16} />
                      <span>{session.transcript_length || session.answered_questions || 0} conversation turns</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="schedule" size={16} />
                      <span>{formatDate(session.completed_at || session.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Performance Indicator */}
                {session.overall_score && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Overall Performance</span>
                      <span className={`font-medium ${getScoreColor(session.overall_score)}`}>
                        {session.overall_grade || 'N/A'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          session.overall_score >= 90 ? 'bg-green-500' :
                          session.overall_score >= 80 ? 'bg-blue-500' :
                          session.overall_score >= 70 ? 'bg-yellow-500' :
                          session.overall_score >= 60 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${session.overall_score}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Action Button - Show for all training sessions */}
                <Button
                  onClick={() => handleViewReport(session.session_id || session.id, session.source)}
                  disabled={loadingDetails && selectedSession === (session.session_id || session.id)}
                  className="w-full flex items-center justify-center gap-2"
                  variant={session.status === 'completed' ? 'primary' : 'secondary'}
                >
                  {loadingDetails && selectedSession === (session.session_id || session.id) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <Icon name="visibility" size={18} />
                      View Report
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReportsPage;
